import { QueryCache, QueryClient, type QueryFunctionContext } from "@tanstack/react-query";

import { hubQueryRetry, hubQueryRetryDelay } from "@/lib/api";
import { bindHubQueryAbortSignal } from "@/lib/hubQueryAbortSignal";

function wrapQueryFnWithAbort(queryFn: unknown): unknown {
  if (typeof queryFn !== "function") {
    return queryFn;
  }
  const tagged = queryFn as { __hubQueryAbortWrapped?: boolean };
  if (tagged.__hubQueryAbortWrapped === true) {
    return queryFn;
  }
  const original = queryFn as (context: QueryFunctionContext) => unknown;
  const wrapped = (context: QueryFunctionContext) =>
    bindHubQueryAbortSignal(context.signal, () => original(context));
  (wrapped as { __hubQueryAbortWrapped?: boolean }).__hubQueryAbortWrapped = true;
  return wrapped;
}

function createHubQueryCache(): QueryCache {
  const cache = new QueryCache();
  const originalBuild = cache.build.bind(cache);
  cache.build = ((...args: Parameters<typeof originalBuild>) => {
    const options = args[1];
    if (options != null && typeof options === "object" && "queryFn" in options) {
      args[1] = {
        ...options,
        queryFn: wrapQueryFnWithAbort(options.queryFn) as typeof options.queryFn,
      };
    }
    return originalBuild(...args);
  }) as typeof cache.build;
  return cache;
}

export const queryClient = new QueryClient({
  queryCache: createHubQueryCache(),
  defaultOptions: {
    queries: {
      retry: hubQueryRetry,
      retryDelay: hubQueryRetryDelay,
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  },
});
